import { Theme, createTheme } from '@mui/material';
import { getServiceName } from '@repo/utils';

import { affiliateThemeColor } from './affiliate';
import { chatServiceColor } from './chatServiceColor';
import { color as tapColor } from './color';
import { commonTheme } from './common';
import { ThemeConfigType, muiBaseOptions, muiThemesOptions } from './mui';
import { tapTheme } from './tap';
import { v2Color } from '../tokens/v2/cssVars';

// BubbleTap / Bubble Studio Theme
export { tapTheme as defaultTheme } from './tap';
export type { TapBaseThemeType } from './tap';

// Chat Affiliate Theme
export { affiliateThemeColor } from './affiliate';

// Chat Service Color (bubblechat + tingle 통합)
export { chatServiceColor } from './chatServiceColor';

// export const themeByService = () => {
//   const themes: Record<string, ChatBaseThemeType | TingleThemeType> = {
//     bubblechat: chatBaseTheme,
//     tingle: tingleTheme,
//   };
//   const serviceName = (import.meta.env.VITE_APP_SERVICE_NAME || 'bubblechat') as string;
//   return themes[serviceName];
// };

type ThemeColor = {
  role: string;
  sub_description?: string;
  light: string;
  dark: string;
};

type RecursiveTheme<T> = {
  [K in keyof T]: T[K] extends { role: string } ? ThemeColor : T[K] extends object ? RecursiveTheme<T[K]> : T[K];
};

type ThemeColorWithService = {
  role: string;
  sub_description?: string;
  bubblechat: { light: string; dark: string };
  tingle: { light: string; dark: string };
};

type ChatColorTheme = RecursiveTheme<typeof chatServiceColor>;
type AffiliateColorTheme = RecursiveTheme<typeof affiliateThemeColor>;

const getColorByService = (serviceName: 'bubblechat' | 'tingle'): ChatColorTheme => {
  const isThemeColorWithService = (val: any): val is ThemeColorWithService =>
    val && typeof val === 'object' && 'role' in val && 'bubblechat' in val && 'tingle' in val;

  const extractTheme = <T extends object>(obj: T): RecursiveTheme<T> => {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key as keyof T];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (isThemeColorWithService(value)) {
          acc[key as keyof T] = {
            role: value.role,
            sub_description: value.sub_description ?? '',
            light: value[serviceName].light,
            dark: value[serviceName].dark,
          } as RecursiveTheme<T>[keyof T];
        } else {
          acc[key as keyof T] = extractTheme(value) as RecursiveTheme<T>[keyof T];
        }
      } else {
        acc[key as keyof T] = value as RecursiveTheme<T>[keyof T];
      }

      return acc;
    }, {} as RecursiveTheme<T>);
  };

  return extractTheme(chatServiceColor);
};

export const themeByService = (serviceNameOverride?: 'bubblechat' | 'tingle') => {
  const serviceName =
    serviceNameOverride || ((import.meta.env.VITE_APP_SERVICE_NAME || 'bubblechat') as 'bubblechat' | 'tingle');
  const baseColor = {
    ...commonTheme.color,
    ...tapTheme.color,
    ...tapColor,
    ...getColorByService(serviceName),
  };
  const theme = {
    ...commonTheme,
    color: {
      ...baseColor,
      // v2 색 토큰 네이밍(신규/변경 컴포넌트 전용). 예: theme.color.brand.primary, theme.color.label.primary.
      brand: v2Color.brand,
      bg: v2Color.bg,
      label: v2Color.label,
      border: v2Color.border,
      system: v2Color.system,
      chat: v2Color.chat,
      currency: v2Color.currency,
      button: v2Color.button,
      highlight: v2Color.highlight,
      // v1과 이름이 겹치는 그룹: 레거시 리프는 그대로 우선 보존하고 v2 전용 리프만 추가(text.accent, gradient.aurora 등).
      static: { ...v2Color.static, ...baseColor.static },
      text: { ...v2Color.text, ...baseColor.text },
      gradient: { ...v2Color.gradient, ...baseColor.gradient },
      rgba: {
        rgba1: 'rgba(255, 255, 255, 0)',
        rgba2: 'rgba(255, 255, 255, 0.20)',
        rgba3: 'rgba(255, 255, 255, 0.20)',
      },
    },
  };

  return theme;
};

export type ChatBaseThemeType = ReturnType<typeof themeByService>;

const getAffiliateColorByService = (serviceName: 'bubblechat' | 'tingle'): AffiliateColorTheme => {
  const isThemeColorWithService = (val: any): val is ThemeColorWithService =>
    val && typeof val === 'object' && 'role' in val && 'bubblechat' in val && 'tingle' in val;

  const extractTheme = <T extends object>(obj: T): RecursiveTheme<T> => {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key as keyof T];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (isThemeColorWithService(value)) {
          acc[key as keyof T] = {
            role: value.role,
            sub_description: value.sub_description ?? '',
            light: value[serviceName].light,
            dark: value[serviceName].dark,
          } as RecursiveTheme<T>[keyof T];
        } else {
          acc[key as keyof T] = extractTheme(value) as RecursiveTheme<T>[keyof T];
        }
      } else {
        acc[key as keyof T] = value as RecursiveTheme<T>[keyof T];
      }

      return acc;
    }, {} as RecursiveTheme<T>);
  };

  return extractTheme(affiliateThemeColor);
};

export const affiliateThemeByService = () => {
  const serviceName = (import.meta.env.VITE_APP_SERVICE_NAME || 'bubblechat') as 'bubblechat' | 'tingle';
  const affiliateBase = {
    ...commonTheme.color,
    ...getAffiliateColorByService(serviceName),
  };
  const theme = {
    ...commonTheme,
    color: {
      ...affiliateBase,
      // v2 색 토큰 네이밍 — 공유 컴포넌트(packages/ui)가 두 테마에서 같은 이름을 쓰도록 chat 테마와 동일하게 노출.
      // 값은 `var(--v2-*)`이며, 리워드 앱 루트의 V2BrandVarsGlobal이 라이트 파생 팔레트를 주입해 해석한다.
      brand: v2Color.brand,
      bg: v2Color.bg,
      label: v2Color.label,
      border: v2Color.border,
      system: v2Color.system,
      chat: v2Color.chat,
      currency: v2Color.currency,
      button: v2Color.button,
      highlight: v2Color.highlight,
      // v1과 이름이 겹치는 그룹: 레거시 리프를 우선 보존하고 v2 전용 리프만 더한다(text.accent 등).
      static: { ...v2Color.static, ...affiliateBase.static },
      text: { ...v2Color.text, ...affiliateBase.text },
      rgba: {
        rgba1: 'rgba(255, 255, 255, 0)',
        rgba2: 'rgba(255, 255, 255, 0.20)',
        rgba3: 'rgba(255, 255, 255, 0.20)',
      },
    },
  };

  return theme;
};

export type AffiliateBaseThemeType = ReturnType<typeof affiliateThemeByService>;

export const muiTheme = (config: ThemeConfigType = {}): Theme => {
  const serviceName = getServiceName();
  let themeOptions = muiThemesOptions[serviceName];

  // fallback to bubblechat theme if service theme not found
  if (!themeOptions) {
    console.warn(new Error(`The theme for ${serviceName} is not valid, falling back to bubblechat`));
    themeOptions = muiThemesOptions['bubblechat'];
  }

  const theme = createTheme(muiBaseOptions, themeOptions);

  return theme;
};
